import {
  SectorFilterInputObject,
  SectorLocationsQueryVariables,
  useSectorLocationsQuery,
  LocationMapParamsInputObject,
  GeojsonFeatureCollection,
  FilterGroupingEnum,
} from 'graphql-common';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { GeojsonFeatureKind } from 'routes/Dashboard/Monitoring/enums';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

export default function useSectorLocationsQueryHook({
  additionalFilters,
  searchQuery,
  skip,
  mapParams,
  usePrevious = true,
  fetchPolicy = 'network-only',
}: ListQueryHookProps & {
  mapParams: LocationMapParamsInputObject | null;
}): ListQueryHookResult<GeojsonFeatureCollection> {
  const sectorsQueryVariables: SectorLocationsQueryVariables = {
    mapParams: mapParams
      ? _pick(mapParams, ['maxLat', 'maxLon', 'minLat', 'minLon', 'zoom'])
      : {
          maxLat: 0,
          maxLon: 0,
          minLat: 0,
          minLon: 0,
          zoom: 0,
        },
    filters: {
      kind: {
        grouping: FilterGroupingEnum.And,
        predicate: { eq: GeojsonFeatureKind.Boundary },
      },
    },
  };

  const filters: SectorFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'areasCode' },
        { fieldName: 'areasName' },
        { fieldName: 'code' },
        { fieldName: 'name' },
        { fieldName: 'userFullName' },
        { fieldName: 'sectorName' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    sectorsQueryVariables.sectorFilters = filters;
  }
  const query = useSectorLocationsQuery({
    fetchPolicy,
    variables: sectorsQueryVariables,
    skip,
  });

  const { loading, data, previousData, error, refetch } = query;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const dataCollection = _get(
    queryData,
    'data',
    {},
  ) as GeojsonFeatureCollection;
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection: [],
    data: dataCollection,
    error,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
